"use client";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ShieldCheck, MessageCircle, Copy, Check } from "lucide-react";
import { FaTelegram } from "react-icons/fa";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { VeriffErrorBoundary } from "@/components/error-boundaries/VeriffErrorBoundary";
import { toast } from "sonner";

interface VerificationDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  verificationUrl: string;
  onCancel?: () => void;
}

export function VerificationDialog({
  isOpen,
  onOpenChange,
  verificationUrl,
  onCancel,
}: VerificationDialogProps) {
  const posthog = usePostHog();
  const router = useRouter();
  const [isLaunching, setIsLaunching] = useState(false);
  const veriffFrameRef = useRef<any>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen && verificationUrl) {
      posthog?.capture("veriff_verification_dialog_shown", {
        verification_url: verificationUrl,
        timestamp: new Date().toISOString(),
      });
    }
  }, [isOpen, verificationUrl, posthog]);

  const ensureSDK = async () => {
    if (typeof window === "undefined") return false;
    if ((window as any).veriffSDK) return true;
    return new Promise<boolean>((resolve) => {
      const existing = document.getElementById("veriff-incontext-sdk");
      if (existing) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.id = "veriff-incontext-sdk";
      script.src = "https://cdn.veriff.me/incontext/js/v2.5.0/veriff.js";
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleVerify = async () => {
    posthog?.capture("veriff_verification_started", {
      verification_url: verificationUrl,
      timestamp: new Date().toISOString(),
    });
    setIsLaunching(true);

    // Close our dialog FIRST to prevent focus lock conflicts with Veriff
    onOpenChange(false);

    try {
      const ready = await ensureSDK();
      if (!ready) {
        posthog?.capture("veriff_incontext_sdk_load_failed", {
          verification_url: verificationUrl,
        });
        setIsLaunching(false);
        return;
      }

      const SDK: any = (window as any).veriffSDK;
      if (!SDK || typeof SDK.createVeriffFrame !== "function") {
        console.error("[VerificationDialog] Veriff SDK not properly loaded");
        posthog?.capture("veriff_sdk_invalid", {
          verification_url: verificationUrl,
        });
        setIsLaunching(false);
        return;
      }

      const M = SDK?.MESSAGES || {};

      // Small delay to ensure our dialog is fully unmounted before Veriff opens
      // This prevents focus lock conflicts
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Get proxy URL using helper
      const proxyUrl = getProxyUrl();

      // Create Veriff frame - store reference so we can close it if needed
      veriffFrameRef.current = SDK.createVeriffFrame({
        url: proxyUrl,
        onEvent: (msg: any) => {
          posthog?.capture("veriff_incontext_event", { event: msg });
          const finished = msg === M.FINISHED || msg === "FINISHED";
          const submitted = msg === M.SUBMITTED || msg === "SUBMITTED";
          const canceled = msg === M.CANCELED || msg === "CANCELED";

          if (finished || submitted || canceled) {
            // Clear ref when Veriff closes naturally
            veriffFrameRef.current = null;
          }

          if (finished || submitted) {
            // Ensure our own dialog stays closed when verification completes
            onOpenChange(false);
          }
          if (canceled) {
            posthog?.capture("veriff_verification_dismissed", {
              method: "incontext_modal",
              verification_url: verificationUrl,
            });
          }
        },
        onReload: () => {
          window.location.reload();
        },
      });
    } catch (error) {
      console.error(
        "[VerificationDialog] Error during verification setup:",
        error
      );
      posthog?.capture("veriff_verification_error", {
        error: error instanceof Error ? error.message : "Unknown error",
        verification_url: verificationUrl,
      });
    } finally {
      setIsLaunching(false);
    }
  };

  const handleClose = () => {
    posthog?.capture("veriff_verification_dismissed", {
      verification_url: verificationUrl,
      timestamp: new Date().toISOString(),
    });

    // Close Veriff frame if it's open (e.g., user clicks Cancel before finishing)
    if (
      veriffFrameRef.current &&
      typeof veriffFrameRef.current.close === "function"
    ) {
      try {
        veriffFrameRef.current.close();
        veriffFrameRef.current = null;
      } catch (error) {
        console.warn("[VerificationDialog] Error closing Veriff frame:", error);
      }
    }

    if (onCancel) {
      onCancel();
    }
    onOpenChange(false);
  };

  const handleInAppSupport = () => {
    onOpenChange(false);
    router.push("/support");
  };

  const handleTelegramSupport = () => {
    window.open("https://t.me/batchfinderdev", "_blank");
  };

  const getProxyUrl = () => {
    try {
      const url = new URL(verificationUrl);
      if (url.hostname.includes("veriff")) {
        return `https://verify.batchfinder.com${url.pathname}${url.search}`;
      }
    } catch (error) {
      console.error(
        "[VerificationDialog] Error parsing verification URL:",
        error
      );
    }
    return verificationUrl;
  };

  const handleCopyLink = async () => {
    const proxyUrl = getProxyUrl();
    try {
      await navigator.clipboard.writeText(proxyUrl);
      setCopied(true);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
      posthog?.capture("veriff_link_copied", {
        verification_url: verificationUrl,
      });
    } catch (error) {
      toast.error("Failed to copy link");
      console.error("[VerificationDialog] Error copying link:", error);
    }
  };

  return (
    <VeriffErrorBoundary>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            posthog?.capture("veriff_verification_dismissed", {
              method: "dialog_controls",
              verification_url: verificationUrl,
              timestamp: new Date().toISOString(),
            });
          }
          onOpenChange(open);
        }}
      >
        <DialogContent className="w-[95vw] max-w-[500px] max-h-[90vh] rounded-xl overflow-y-auto">
          <DialogHeader className="pb-3">
            <DialogTitle className="flex items-center gap-2 text-left">
              <ShieldCheck className="h-5 w-5 text-primary flex-shrink-0" />
              <span className="break-words">Identity Verification</span>
            </DialogTitle>

            <div className="text-left space-y-3 text-sm sm:text-base pt-2 text-muted-foreground">
              <p className="break-words">
                Instacart needs one selfie to unlock your bot.
              </p>
              <p className="text-red-600 font-medium break-words">
                Skip or fail this step ➜ you'll see "User not allowed to go
                online" when you tap Start Bot.
              </p>
              <div className="space-y-2">
                <p className="font-semibold text-foreground break-words">
                  What happens after you take the photo
                </p>
                <p className="break-words">
                  Instacart shows "Thank you! Your verification data has been
                  submitted."
                </p>
                <p className="flex items-start gap-2">
                  <span className="flex-shrink-0">🔸</span>
                  <span className="break-words">
                    That means the photo was sent, not that it was approved.
                  </span>
                </p>
                <p className="font-medium break-words">
                  Review takes up to 3 minutes.
                </p>
              </div>
              <p className="break-words">
                If the Start-Bot button still throws the "User not allowed"
                error, the selfie is stuck or rejected.
              </p>
            </div>
          </DialogHeader>

          <div className="pb-2 space-y-2">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="help" className="border-0">
                <AccordionTrigger className="py-3 text-sm font-medium hover:no-underline">
                  Still stuck? 🤔
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  <div className="space-y-4">
                    <div className="flex flex-col gap-3">
                      <Button
                        className="flex items-center justify-center gap-2 min-h-[40px] text-xs sm:text-sm bg-primary hover:bg-primary/90"
                        onClick={handleInAppSupport}
                        size="sm"
                      >
                        <MessageCircle className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">Talk to Support</span>
                      </Button>
                      <Button
                        variant="outline"
                        className="flex items-center justify-center gap-2 min-h-[40px] text-xs sm:text-sm"
                        onClick={handleTelegramSupport}
                        size="sm"
                      >
                        <FaTelegram className="h-4 w-4 text-[#229ED9] flex-shrink-0" />
                        <span className="truncate">Telegram Support</span>
                      </Button>
                    </div>
                    <div className="text-xs sm:text-sm text-muted-foreground space-y-2">
                      <p className="break-words">Get help right away:</p>
                      <ul className="space-y-1 ml-4">
                        <li className="break-words">
                          • <strong>"Talk to Support"</strong> - Chat with our
                          team in-app
                        </li>
                        <li className="break-words">
                          • <strong>"Telegram Support"</strong> - Connect via
                          Telegram (@BatchFinderDev)
                        </li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="advanced" className="border-0">
                <AccordionTrigger className="py-3 text-sm font-medium hover:no-underline">
                  Advanced Options ⚙️
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  <div className="space-y-3">
                    <p className="text-xs sm:text-sm text-muted-foreground break-words">
                      If the verification modal doesn't open automatically, you
                      can copy the link and open it manually in your browser.
                    </p>
                    <div className="flex flex-col gap-2">
                      <div className="bg-muted p-3 rounded-md break-all text-xs font-mono">
                        {getProxyUrl()}
                      </div>
                      <Button
                        variant="outline"
                        className="w-full flex items-center justify-center gap-2 min-h-[40px] text-xs sm:text-sm"
                        onClick={handleCopyLink}
                        size="sm"
                      >
                        {copied ? (
                          <>
                            <Check className="h-4 w-4 flex-shrink-0 text-green-600" />
                            <span className="truncate">Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 flex-shrink-0" />
                            <span className="truncate">Copy Link</span>
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          <div className="pt-2">
            <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2">
              <Button
                variant="outline"
                className="w-full sm:w-auto min-h-[40px] text-sm"
                onClick={handleClose}
                size="sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleVerify}
                disabled={isLaunching}
                className="w-full sm:w-auto bg-primary hover:bg-primary/90 flex items-center justify-center gap-2 min-h-[40px] text-sm font-medium"
                size="sm"
              >
                <span className="truncate">
                  {isLaunching ? "Loading..." : "Verify Identity"}
                </span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </VeriffErrorBoundary>
  );
}
